/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import { useSoftUIController } from "context";
import { getUserData } from "Utils/helpers";

function Breadcrumbs({ icon, title, route, light }) {
  const routes = route.slice(0, -1);

  const [controller, dispatch] = useSoftUIController();
  const { platform } = controller;
  const { hash, pathname } = useLocation();
  const userData = getUserData();

  const hidePlatformNamePaths =
    userData && userData.roles.find((item) => item.id == 1)
      ? [
          "/account",
          "/management/platforms",
          "/management/users",
          "/management/users/users-logs",
          "/management/users/edit/:id",
          "/management/export/competitions",
          "/management/export/participants",
          "/feed-settings/templates/:id/edit-filter/:id",
          "/feed-settings/filters/create-filter",
          "/feed-api/documentation",
          "/feed-api/feed-integration",
          "/feed-api/changelog",
          "/settlement/currentv:id",
          "/settlement/current",
          "/settlement/odd",
          "/settlement/finished",
          "/settlement/outrights",
          "/settlement/stucked-fixtures",
          "/settlement/advanced",
          "/settlement/fixture",
          "/settlement/confirmations",
          "/monitoring/fixture-live-view/",
        ]
      : [
          "/account",
          "/management/platforms",
          "/management/users",
          "/management/users/users-logs",
          "/management/users/edit/:id",
          "/management/export/competitions",
          "/management/export/participants",
          "/feed-settings/templates/:id/edit-filter/:id",
          "/feed-settings/filters/create-filter",
          "/feed-api/documentation",
          "/feed-api/feed-integration",
          "/feed-api/changelog",
          "/settlement/currentv:id",
          "/settlement/current",
          "/settlement/odd",
          "/settlement/finished",
          "/settlement/outrights",
          "/settlement/stucked-fixtures",
          "/settlement/advanced",
          "/settlement/fixture",
          "/settlement/confirmations",
          "/monitoring/fixture-live-view/",
        ];

  function showPlatform() {
    //console.log(pathname);
    let path = pathname.replace(/[0-9]+/g, ":id");
    if (path && hidePlatformNamePaths.indexOf(path) > -1) return false;
    return true;
  }

  return (
    <SoftBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        <Link to="/dashboard">
          <SoftTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </SoftTypography>
          <SoftTypography
            component="span"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0, display: showPlatform() ? "" : "none" }}
            className="platform-title"
          >
            {platform ? platform.label : ""}
          </SoftTypography>
        </Link>

        {routes.map((el, index) => (
          <Link to={`/${routes.join("/")}`} key={el}>
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {el.replace(/-/g, " ").replace(/_/g, " ")}
            </SoftTypography>
          </Link>
        ))}
        <SoftTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {title.replace(/-/g, " ").replace(/_/g, " ")}
        </SoftTypography>
      </MuiBreadcrumbs>
    </SoftBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
